import { ApolloLink } from '@apollo/client';
import type { GraphQLFormattedError } from 'graphql';
import { map } from 'rxjs';

const UNRESOLVABLE_LINK = 'UNRESOLVABLE_LINK';

function isUnresolvableLink(error: GraphQLFormattedError): boolean {
    const contentful = error.extensions?.['contentful'] as { code?: string } | undefined;
    return contentful?.code === UNRESOLVABLE_LINK;
}

/**
 * Contentful returns partial data plus an `UNRESOLVABLE_LINK` GraphQL error when an entry
 * references another entry that is unpublished or deleted (for example a journal post whose
 * author page has been taken down). The unresolvable field comes back `null` in `data`, which
 * the UI already handles.
 *
 * This link drops only those errors, logging each one, so a dangling reference can't fail the
 * whole static build. Any other GraphQL error is passed through untouched and still rejects the
 * query under Apollo's default `errorPolicy: 'none'`. Network errors (rate limits, auth) never
 * reach this map and are unaffected.
 */
export const ignoreUnresolvableLinks = new ApolloLink((operation, forward) =>
    forward(operation).pipe(
        map((result) => {
            if (!result.errors?.length) return result;

            const unresolvable = result.errors.filter(isUnresolvableLink);
            if (unresolvable.length === 0) return result;

            for (const error of unresolvable) {
                console.warn(`[contentful] ${operation.operationName}: ${error.message}`);
            }

            const remaining = result.errors.filter((error) => !isUnresolvableLink(error));
            return { ...result, errors: remaining.length ? remaining : undefined };
        })
    )
);
